'use client'

import { ChevronUpIcon } from '@/assets/icons'
import { Dropdown, DropdownContent, DropdownTrigger } from '@/components/ui/dropdown'
import { cn } from '@/lib/utils'
import { Link } from '@/i18n/navigation'
import { useState } from 'react'
import { LogOutIcon, SettingsIcon, UserIcon } from './icons'
import { useRouter } from '@/i18n/navigation'
import { useUser } from '@/services/api/user'
import { logout } from '@/services/api/(auth)/logout'
import { clearAuthSlice } from '@/store/slices/auth'

export function UserInfo() {
  const [isOpen, setIsOpen] = useState(false)
  const router = useRouter()
  const { user } = useUser()

  async function handleLogout() {
    try {
      await logout()
    } finally {
      await clearAuthSlice()
      router.push('/login')
    }
  }

  const initials = `${user?.full_name?.[0] ?? ''}`.toUpperCase()

  return (
    <Dropdown isOpen={isOpen} setIsOpen={setIsOpen}>
      <DropdownTrigger className="ring-primary dark:ring-offset-gray-dark rounded-full align-middle ring-offset-2 outline-none focus-visible:ring-1">
        <span className="sr-only">My Account</span>

        <figure className="flex items-center gap-2.5">
          <span className="grid size-9 shrink-0 place-items-center rounded-full bg-gradient-to-br from-[#00825B] to-[#B3021C] text-sm font-semibold text-white shadow-sm">
            {initials || <UserIcon width={16} height={16} />}
          </span>
          <figcaption className="text-white hidden items-center gap-1 font-medium sm:flex">
            <span>
              {user?.full_name}
            </span>

            <ChevronUpIcon
              aria-hidden
              className={cn('rotate-180 transition-transform', isOpen && 'rotate-0')}
              strokeWidth={1.5}
            />
          </figcaption>
        </figure>
      </DropdownTrigger>

      <DropdownContent
        className="border-stroke/70 dark:border-white/8 dark:bg-gray-dark overflow-hidden rounded-2xl border bg-white shadow-xl min-[230px]:min-w-[17.5rem]"
        align="end">
        <h2 className="sr-only">User information</h2>

        <figure className="flex items-center gap-3 bg-gradient-to-br from-[#2F81F1]/10 to-[#7C3AED]/10 px-5 py-4">
          <span className="grid size-11 shrink-0 place-items-center rounded-full bg-gradient-to-br from-[#00825B] to-[#B3021C] text-base font-semibold text-white shadow-sm">
            {initials || <UserIcon width={18} height={18} />}
          </span>
          <figcaption className="min-w-0 space-y-1 text-base font-medium">
            <div className="text-dark truncate leading-none font-semibold dark:text-white">
              {user?.full_name}
            </div>
            <div className="text-primary inline-block rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold capitalize">
              {user?.profile}
            </div>
            <div className="truncate text-sm text-gray-600 dark:text-dark-6">{user?.email}</div>
          </figcaption>
        </figure>

        <hr className="dark:border-dark-3 border-[#E8E8E8]" />

        <div className="dark:text-dark-6 p-2 text-base text-[#4B5563] [&>*]:cursor-pointer">
          <Link
            href={'/profile'}
            onClick={() => setIsOpen(false)}
            className="hover:bg-gray-2 hover:text-dark dark:hover:bg-dark-3 flex w-full items-center gap-2.5 rounded-xl px-2.5 py-[9px] dark:hover:text-white">
            <SettingsIcon />

            <span className="mr-auto text-base font-medium">Account Settings</span>
          </Link>
        </div>

        <hr className="dark:border-dark-3 border-[#E8E8E8]" />

        <div className="dark:text-dark-6 p-2 text-base text-[#4B5563]">
          <button
            className="flex w-full items-center gap-2.5 rounded-xl px-2.5 py-[9px] transition-colors hover:bg-red/10 hover:text-red"
            onClick={async () => {
              await handleLogout()
              setIsOpen(false)
            }}>
            <LogOutIcon />

            <span className="text-base font-medium">Log out</span>
          </button>
        </div>
      </DropdownContent>
    </Dropdown>
  )
}
